import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CustomCursor from '@/components/CustomCursor';
import { Shield, Mail, Phone, MapPin, AlertCircle } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const lastUpdated = "January 11, 2026";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50">
      <CustomCursor />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-16 pb-12 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-100/50 via-transparent to-purple-100/50"></div>
          <div className="max-w-4xl mx-auto px-6 lg:px-8 relative">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full mb-6">
                <Shield className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-semibold text-purple-800">Your Privacy Matters</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                Privacy Policy
              </h1>
              <p className="text-gray-600">
                Last Updated: {lastUpdated}
              </p>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              
              {/* Introduction */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Encore Services, LLC ("Company," "we," "us," or "our") respects your privacy and is committed to protecting your personal information in compliance with applicable United States federal and state privacy laws. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website acusightpro.com and use our AcuSightPro platform (collectively, the "Services").
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Please read this Privacy Policy carefully. By accessing or using our Services, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with the terms of this Privacy Policy, please do not access or use our Services.
                </p>
              </div>

              {/* Information We Collect */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Personal Information You Provide</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We collect personal information that you voluntarily provide to us when you:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li>Fill out the contact form on our website (name, email address, message content)</li>
                  <li>Request a demonstration or pilot of AcuSightPro</li>
                  <li>Communicate with us via email, telephone, or other channels</li>
                  <li>Register for an account or subscribe to our services</li>
                  <li>Participate in surveys, promotions, or other activities</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">Categories of Personal Information Collected</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  In accordance with the California Consumer Privacy Act (CCPA) and other state privacy laws, we may collect the following categories of personal information:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li><strong>Identifiers:</strong> Name, email address, phone number, IP address, account name</li>
                  <li><strong>Professional Information:</strong> Job title, employer, government agency affiliation</li>
                  <li><strong>Internet Activity:</strong> Browsing history on our Services, search history, interaction with our website</li>
                  <li><strong>Geolocation Data:</strong> General location based on IP address</li>
                  <li><strong>Inferences:</strong> Preferences, characteristics, and behavior derived from collected information</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">Information Automatically Collected</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  When you access our Services, we may automatically collect certain information, including:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li><strong>Device Information:</strong> Browser type, operating system, device type, and unique device identifiers</li>
                  <li><strong>Log Data:</strong> IP address, access times, pages viewed, referring URL, and actions taken on the Services</li>
                  <li><strong>Usage Information:</strong> Features used, time spent on pages, and navigation patterns</li>
                  <li><strong>Location Information:</strong> General geographic location based on IP address</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">Cookies and Similar Technologies</h3>
                <p className="text-gray-600 leading-relaxed">
                  We use cookies, web beacons, and similar tracking technologies to collect information about your browsing activities. You can control cookies through your browser settings, but disabling cookies may limit your ability to use certain features of our Services. We do not respond to "Do Not Track" signals at this time, as there is no industry standard for such signals.
                </p>
              </div>

              {/* How We Use Your Information */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We use the information we collect for the following business purposes:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li><strong>Provide and Maintain Services:</strong> To deliver our platform, process transactions, and maintain your account</li>
                  <li><strong>Respond to Inquiries:</strong> To respond to your questions, comments, and requests submitted through our contact form</li>
                  <li><strong>Communication:</strong> To send administrative information, updates, and marketing communications (with your consent where required)</li>
                  <li><strong>Improve Services:</strong> To understand how users interact with our Services and to improve functionality and user experience</li>
                  <li><strong>Security:</strong> To detect, prevent, and address technical issues, fraud, and security threats</li>
                  <li><strong>Legal Compliance:</strong> To comply with applicable federal and state laws, regulations, and legal processes</li>
                  <li><strong>Business Operations:</strong> For internal administration, auditing, and data analysis</li>
                  <li><strong>Contractual Obligations:</strong> To fulfill our obligations under government contracts and agreements</li>
                </ul>
              </div>

              {/* Disclosure of Your Information */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Disclosure of Your Information</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We may share your information in the following circumstances:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li><strong>Service Providers:</strong> With third-party vendors who perform services on our behalf (e.g., hosting, analytics, customer support), bound by contractual obligations to protect your information</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of all or a portion of our assets</li>
                  <li><strong>Legal Requirements:</strong> When required by law, subpoena, court order, or other legal process, or to protect our rights, privacy, safety, or property</li>
                  <li><strong>With Your Consent:</strong> When you have given us explicit permission to share your information</li>
                  <li><strong>Government Clients:</strong> As necessary to fulfill our contractual obligations to federal government clients, in compliance with applicable regulations including FAR and agency-specific requirements</li>
                </ul>
                
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-6">
                  <p className="text-purple-800 font-semibold mb-2">We Do Not Sell Your Personal Information</p>
                  <p className="text-purple-700 text-sm">
                    We do not sell, rent, or trade your personal information to third parties for monetary or other valuable consideration as defined under the CCPA and other state privacy laws.
                  </p>
                </div>
              </div>

              {/* Data Security */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Security</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We implement appropriate technical and organizational security measures designed to protect your personal information in accordance with federal standards, including:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Encryption in transit (TLS 1.2+) and at rest using FIPS 140-2 validated cryptographic modules</li>
                  <li>Role-based and attribute-based access controls (RBAC/ABAC)</li>
                  <li>Logical tenant isolation and strict separation of development, test, and production environments</li>
                  <li>Regular security assessments and vulnerability scanning</li>
                  <li>Audit logging for all system activities</li>
                  <li>Security controls aligned with NIST SP 800-53 guidelines</li>
                </ul>
                <p className="text-gray-600 leading-relaxed mt-4">
                  However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
                </p>
              </div>

              {/* Data Retention */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Retention</h2>
                <p className="text-gray-600 leading-relaxed">
                  We retain your personal information for as long as necessary to fulfill the purposes for which it was collected, comply with our legal and contractual obligations (including federal records retention requirements where applicable), resolve disputes, and enforce our agreements. Contact form submissions are retained for a reasonable period to allow us to respond to your inquiry and for record-keeping purposes. When personal information is no longer needed, we securely delete or anonymize it in accordance with our data retention policies and applicable law.
                </p>
              </div>

              {/* State Privacy Rights - CCPA Section */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Privacy Rights Under U.S. State Laws</h2>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-blue-800 text-sm">
                      The following rights apply to residents of states with comprehensive privacy laws, including California (CCPA/CPRA), Virginia (VCDPA), Colorado (CPA), Connecticut (CTDPA), and Utah (UCPA).
                    </p>
                  </div>
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">California Residents (CCPA/CPRA)</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  If you are a California resident, you have the following rights under the California Consumer Privacy Act (CCPA) as amended by the California Privacy Rights Act (CPRA):
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li><strong>Right to Know:</strong> You have the right to request information about the categories and specific pieces of personal information we have collected about you, as well as the sources, purposes, and third parties with whom we share it.</li>
                  <li><strong>Right to Delete:</strong> You have the right to request deletion of your personal information, subject to certain exceptions (e.g., legal obligations, completing transactions).</li>
                  <li><strong>Right to Correct:</strong> You have the right to request correction of inaccurate personal information.</li>
                  <li><strong>Right to Opt-Out of Sale/Sharing:</strong> You have the right to opt out of the "sale" or "sharing" of your personal information. Note: We do not sell or share your personal information as defined by the CCPA.</li>
                  <li><strong>Right to Limit Use of Sensitive Personal Information:</strong> If we collect sensitive personal information, you have the right to limit its use and disclosure.</li>
                  <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights.</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">Virginia, Colorado, Connecticut, and Utah Residents</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Residents of Virginia, Colorado, Connecticut, and Utah have similar rights under their respective state privacy laws, including:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li>Right to access your personal data</li>
                  <li>Right to correct inaccuracies in your personal data</li>
                  <li>Right to delete your personal data</li>
                  <li>Right to data portability</li>
                  <li>Right to opt out of targeted advertising, sale of personal data, and profiling</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">How to Exercise Your Rights</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  To exercise any of your privacy rights, you may submit a verifiable consumer request by:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
                  <li>Emailing us at: <a href="mailto:info@encoresvcsllc.com" className="text-purple-600 hover:underline">info@encoresvcsllc.com</a></li>
                  <li>Calling us at: <a href="tel:+12024608668" className="text-purple-600 hover:underline">(202) 460-8668</a></li>
                  <li>Submitting a request through our <a href="/contact" className="text-purple-600 hover:underline">Contact Form</a></li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  We will verify your identity before processing your request. You may designate an authorized agent to make a request on your behalf, subject to identity verification. We will respond to your request within the timeframes required by applicable law (generally 45 days, with possible extension).
                </p>
              </div>

              {/* CAN-SPAM Compliance */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">CAN-SPAM Act Compliance</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We comply with the CAN-SPAM Act of 2003. When we send commercial email communications:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>We will not use false or misleading subjects or email addresses</li>
                  <li>We will identify the message as an advertisement when required</li>
                  <li>We will include our physical business address</li>
                  <li>We will honor opt-out/unsubscribe requests promptly (within 10 business days)</li>
                  <li>We will monitor third-party email marketing services for compliance</li>
                </ul>
                <p className="text-gray-600 leading-relaxed mt-4">
                  To opt out of marketing emails, click the "unsubscribe" link at the bottom of any marketing email or contact us directly. Note that you may still receive transactional or administrative emails.
                </p>
              </div>

              {/* COPPA Compliance */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy (COPPA Compliance)</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Our Services are intended for business use and are not directed to children under the age of 13. In compliance with the Children's Online Privacy Protection Act (COPPA):
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>We do not knowingly collect personal information from children under 13</li>
                  <li>If we become aware that we have collected personal information from a child under 13 without parental consent, we will take immediate steps to delete that information</li>
                  <li>If you believe we may have collected information from a child under 13, please contact us immediately</li>
                </ul>
              </div>

              {/* Federal Government Data */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Federal Government Data Handling</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  When providing services to federal government agencies, we comply with additional requirements including:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li><strong>Privacy Act of 1974:</strong> When applicable, we maintain systems of records in compliance with the Privacy Act</li>
                  <li><strong>Federal Information Security Modernization Act (FISMA):</strong> Our security controls are aligned with NIST frameworks as required</li>
                  <li><strong>Federal Acquisition Regulation (FAR):</strong> We comply with applicable FAR clauses regarding data protection and privacy</li>
                  <li><strong>Agency-Specific Requirements:</strong> We adhere to additional privacy and security requirements specified in individual agency contracts</li>
                </ul>
              </div>

              {/* Third-Party Links */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Links</h2>
                <p className="text-gray-600 leading-relaxed">
                  Our Services may contain links to third-party websites and services that are not owned or controlled by Encore Services, LLC. We are not responsible for the privacy practices or content of these third parties. We encourage you to review the privacy policies of any third-party sites you visit. This Privacy Policy applies solely to information collected by our Services.
                </p>
              </div>

              {/* Changes to This Privacy Policy */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
                <p className="text-gray-600 leading-relaxed">
                  We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by posting the updated Privacy Policy on this page with a new "Last Updated" date. For material changes, we may also provide additional notice (such as email notification). Your continued use of our Services after any changes constitutes your acceptance of the updated Privacy Policy.
                </p>
              </div>

              {/* Contact Information */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  If you have questions, concerns, or complaints about this Privacy Policy or our privacy practices, or if you wish to exercise your privacy rights, please contact us:
                </p>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-3">Encore Services, LLC</h3>
                  <div className="space-y-3 text-gray-600">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p>9500 Medical Center Drive, Suite 300</p>
                        <p>Largo, MD 20774</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-purple-600 flex-shrink-0" />
                      <a href="tel:+12024608668" className="text-purple-600 hover:underline">(202) 460-8668</a>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-purple-600 flex-shrink-0" />
                      <a href="mailto:info@encoresvcsllc.com" className="text-purple-600 hover:underline">info@encoresvcsllc.com</a>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
